import { Stack, styled, Typography, TypographyProps } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress, { circularProgressClasses } from '@mui/material/CircularProgress';

type ScoreColor = 'error' | 'success' | 'warning';

interface AssessmentScoreProps {
  value: number;
  valueTypographyVariant?: TypographyProps['variant'];
  size?: number;
  successThreshold?: number;
  errorThreshold?: number;
  color?: ScoreColor;
  label?: string;
}

const StyledBackdrop = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.grey[200],
  ...theme.applyStyles('dark', {
    color: theme.palette.grey[800],
  }),
}));

const StyledProgress = styled(CircularProgress)({
  position: 'absolute',
  left: 0,
  [`& .${circularProgressClasses.circle}`]: {
    strokeLinecap: 'round',
  },
});

const ValueWrapper = styled(Stack)({
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  position: 'absolute',
});

const getColorFromValue = (value: number, errorThreshold: number, successThreshold: number): ScoreColor => {
  if (value <= errorThreshold) return 'error';
  if (value >= successThreshold) return 'success';
  return 'warning';
};

function ScoreValue({
  value,
  label,
  valueTypographyVariant,
}: Pick<AssessmentScoreProps, 'value' | 'label' | 'valueTypographyVariant'>) {
  return (
    <ValueWrapper
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      role="status"
      aria-label={`${label}: ${Math.round(value)}`}
    >
      <Typography sx={{ lineHeight: 1 }} variant={valueTypographyVariant ?? 'helperText'} component="div">
        {Math.round(value)}
      </Typography>
      {label && (
        <Typography sx={{ fontSize: 8, lineHeight: 1 }} component="div" color="text.muted">
          {label}
        </Typography>
      )}
    </ValueWrapper>
  );
}

export default function AssessmentScore({
  value,
  size = 45,
  successThreshold = 60,
  errorThreshold = 30,
  color: colorOverride,
  label,
  valueTypographyVariant,
}: AssessmentScoreProps) {
  const normalizedValue = Math.min(Math.max(value, 0), 100);
  const displayColor = colorOverride ?? getColorFromValue(normalizedValue, errorThreshold, successThreshold);

  return (
    <Box sx={{ position: 'relative', width: size, height: size }} role="presentation">
      <StyledBackdrop variant="determinate" size={size} thickness={3} value={100} aria-hidden="true" />
      <StyledProgress
        variant="determinate"
        size={size}
        thickness={3}
        value={normalizedValue}
        color={displayColor}
        aria-hidden="true"
      />
      <ScoreValue value={normalizedValue} label={label} valueTypographyVariant={valueTypographyVariant} />
    </Box>
  );
}
